// src/components/FeaturesSection.jsx
import React from 'react';
import { Shield, Smartphone, CreditCard, TrendingUp } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const FeaturesSection = ({ language, t }) => {
  const isRTL = language === 'ar';
  const [featuresRef, featuresVisible] = useScrollAnimation(0.2);
  const [titleRef, titleVisible] = useScrollAnimation(0.3);
  
  const features = [
    { icon: Shield, ...t.features.items[0], color: 'from-[#2beaf4] to-[#582f8f]' },
    { icon: Smartphone, ...t.features.items[1], color: 'from-[#582f8f] to-[#1b0976]' },
    { icon: CreditCard, ...t.features.items[2], color: 'from-[#2beaf4] to-[#1b0976]' },
    { icon: TrendingUp, ...t.features.items[3], color: 'from-[#1b0976] to-[#582f8f]' }
  ];

  return (
    <section 
      id="about" 
      className={`py-20 bg-gradient-to-br from-gray-50 to-[#2beaf4]/10 ${isRTL ? 'text-right' : 'text-left'}`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div 
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1b0976] mb-4">
            <span className="bg-gradient-to-r from-[#2beaf4] to-[#582f8f] bg-clip-text text-transparent">
              {t.features.title}
            </span>
          </h2>
          <p className="text-xl text-[#1b0976]/70 max-w-2xl mx-auto">
            {t.features.subtitle}
          </p>
        </div>

        {/* Features Grid */}
        <div ref={featuresRef} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className={`group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-700 ease-out transform hover:-translate-y-3 border border-gray-100 hover:border-[#2beaf4]/30 ${
                featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ 
                transitionDelay: `${index * 200}ms`,
                animationDelay: `${index * 200}ms`
              }}
            >
              {/* Icon Container */}
              <div className="relative mb-6">
                <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-500 transform group-hover:scale-110 group-hover:rotate-3`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                {/* Glow effect */}
                <div className={`absolute inset-0 w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 blur-xl`}></div>
              </div>
              
              {/* Content */}
              <div className="space-y-3">
                <h3 className="text-xl font-semibold text-[#1b0976] group-hover:text-[#582f8f] transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-[#1b0976]/70 leading-relaxed group-hover:text-[#1b0976] transition-colors duration-300">
                  {feature.description}
                </p>
              </div>
              
              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#2beaf4]/0 to-[#582f8f]/0 group-hover:from-[#2beaf4]/5 group-hover:to-[#582f8f]/5 rounded-2xl transition-all duration-500 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Additional decorative elements */}
        <div className="relative mt-16">
          <div className="absolute left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-[#2beaf4] to-[#582f8f] rounded-full opacity-50"></div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;